import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FilePdf, FileXls, ArrowClockwise } from "@phosphor-icons/react";
import { firstOfMonth, todayStr } from "@/components/DateRangeFilter";
import { useDownload, usePdfPreview } from "@/pages/AdvancedReports";

const ACTION_CLS = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-amber-50 text-amber-700 border-amber-200",
  delete: "bg-rose-50 text-rose-700 border-rose-200",
};
const MODULES = ["Penjualan", "Pembelian", "Kas & Bank", "Akuntansi", "Persediaan", "Aset Tetap", "Penggajian"];
const ALL = "__all";
const val = (v) => (typeof v === "number" ? formatIDR(v) : "—");

function FilterSelect({ label, value, onChange, items, testid }) {
  return (
    <div className="min-w-[150px]"><Label className="text-xs">{label}</Label>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Semua</SelectItem>
          {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LogRow({ l }) {
  const changed = l.action === "update" && typeof l.before_total === "number" && typeof l.after_total === "number" && l.before_total !== l.after_total;
  return (
    <TableRow data-testid={`audit-row-${l.id}`}>
      <TableCell className="whitespace-nowrap text-xs"><div className="font-medium">{formatDate(l.date_local)}</div><div className="text-slate-500 tabular-nums">{l.time_local} WIB</div></TableCell>
      <TableCell className="text-sm"><div className="font-medium">{l.user_name}</div><div className="text-[11px] text-slate-500">{l.user_email}</div></TableCell>
      <TableCell><span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${ACTION_CLS[l.action]}`} data-testid={`audit-action-${l.id}`}>{l.action_label}</span></TableCell>
      <TableCell className="text-sm"><div>{l.doc_type}</div><div className="text-[11px] text-slate-500">{l.module}{l.note && ` · ${l.note}`}</div></TableCell>
      <TableCell className="font-mono-plex text-xs whitespace-nowrap">{l.doc_number || "—"}{l.doc_date && <div className="text-[11px] text-slate-500 font-sans">{formatDate(l.doc_date)}</div>}</TableCell>
      <TableCell className="text-sm">{l.partner_name || "—"}</TableCell>
      <TableCell className="tabular-nums text-right whitespace-nowrap text-slate-500">{val(l.before_total)}</TableCell>
      <TableCell className={`tabular-nums text-right whitespace-nowrap ${changed ? "font-semibold text-amber-700" : ""}`}>{val(l.after_total)}</TableCell>
    </TableRow>
  );
}

export default function AuditLog() {
  const [f, setF] = useState({ date_from: firstOfMonth(), date_to: todayStr(), user_id: "", action: "", module: "" });
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const download = useDownload();
  const [preview, previewDialog] = usePdfPreview();
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();

  const [denied, setDenied] = useState(false);
  const load = () => {
    setData(null);
    api.get(`/audit-logs?${qs}`).then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); setData({ logs: [], count: 0 }); });
  };
  useEffect(() => { api.get("/audit-logs/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [qs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (denied) {
    return (
      <div className="p-8" data-testid="audit-log-denied">
        <h1 className="font-serif-display text-3xl font-semibold text-slate-900">Akses ditolak</h1>
        <p className="text-sm text-slate-500 mt-2">Audit Log hanya bisa dibuka oleh Owner dan Admin workspace.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl space-y-5" data-testid="audit-log-page">
      <div>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold text-slate-900">Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">Jejak semua aktivitas transaksi: siapa, kapan, dan apa yang dibuat, diubah, atau dihapus.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3" data-testid="audit-filters">
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="audit-date-from" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="audit-date-to" /></div>
        <FilterSelect label="User" value={f.user_id} onChange={(v) => setF({ ...f, user_id: v })} items={users.map((u) => [u.id, u.name])} testid="audit-user-filter" />
        <FilterSelect label="Status" value={f.action} onChange={(v) => setF({ ...f, action: v })} items={[["create", "Membuat"], ["update", "Merubah"], ["delete", "Menghapus"]]} testid="audit-action-filter" />
        <FilterSelect label="Modul" value={f.module} onChange={(v) => setF({ ...f, module: v })} items={MODULES.map((m) => [m, m])} testid="audit-module-filter" />
        <div className="flex gap-2 ml-auto">
          <Button variant="ghost" size="sm" onClick={load} data-testid="audit-refresh-btn"><ArrowClockwise size={14} /></Button>
          <Button variant="outline" size="sm" onClick={() => preview(`/audit-logs?${qs}&format=pdf`, "audit_log.pdf")} data-testid="audit-pdf-btn"><FilePdf size={14} className="mr-1" />Cetak PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download(`/audit-logs?${qs}&format=xlsx`, "audit_log.xlsx")} data-testid="audit-xlsx-btn"><FileXls size={14} className="mr-1" />Excel</Button>
        </div>
      </div>
      {previewDialog}
      <div className="text-xs text-slate-500" data-testid="audit-count">{data ? `${data.count} aktivitas` : "Memuat…"}</div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <Table className="min-w-[1000px]">
          <TableHeader>
            <TableRow>
              <TableHead>Tanggal Modifikasi</TableHead><TableHead>User</TableHead><TableHead>Status</TableHead><TableHead>Tipe Transaksi</TableHead>
              <TableHead>No. Dokumen</TableHead><TableHead>Partner</TableHead><TableHead className="text-right">Nilai Sebelum</TableHead><TableHead className="text-right">Nilai Sesudah</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.logs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500 text-sm">Belum ada aktivitas di periode ini.</TableCell></TableRow>}
            {data?.logs.map((l) => <LogRow key={l.id} l={l} />)}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
